import { useEffect, useRef, useState } from 'react';
import { CONCEPT_TEXT } from '@/mocks/content';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Concept() {
  const { ref, inView } = useInView(0.15);

  return (
    <section id="concept" className="relative bg-[#0D1B2A] py-24 md:py-36 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=abstract%20dark%20texture%20deep%20navy%20black%20subtle%20pattern%20luxury%20background%20elegant%20minimal&width=1920&height=1080&seq=bg001&orientation=landscape')`,
          backgroundSize: 'cover',
        }}
      />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Gold top line drawing animation */}
        <div className="flex justify-center mb-16">
          <div
            className="h-px bg-[#C9A84C]/60 transition-all duration-1000 ease-out"
            style={{ width: inView ? '120px' : '0px' }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left — Text */}
          <div
            className="transition-all duration-1000 ease-out"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(40px)',
              transitionDelay: '200ms',
            }}
          >
            <p
              className="text-[#C9A84C] text-xs tracking-[0.5em] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              {CONCEPT_TEXT.label}
            </p>
            <h2
              className="text-white text-3xl md:text-4xl font-bold tracking-widest mb-8"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              {CONCEPT_TEXT.heading}
            </h2>

            <div className="space-y-4 mb-10">
              {CONCEPT_TEXT.body.map((para, i) => (
                <p
                  key={i}
                  className="text-white/70 leading-[2] text-sm md:text-base"
                  style={{ fontFamily: "'Shippori Mincho', serif" }}
                >
                  {para}
                </p>
              ))}
            </div>

            {/* Mama's message */}
            <div className="border-l-2 border-[#C9A84C]/60 pl-6 py-2">
              <p
                className="text-xs text-[#C9A84C] tracking-[0.4em] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                {CONCEPT_TEXT.mamaSays.label}
              </p>
              <p
                className="text-white/80 text-sm leading-[2.2] whitespace-pre-line"
                style={{ fontFamily: "'Shippori Mincho', serif" }}
              >
                {CONCEPT_TEXT.mamaSays.text}
              </p>
              <p
                className="text-[#C9A84C]/70 text-xs mt-3 tracking-widest"
                style={{ fontFamily: "'Shippori Mincho', serif" }}
              >
                {CONCEPT_TEXT.mamaSays.name}
              </p>
            </div>
          </div>

          {/* Right — Image card */}
          <div
            className="transition-all duration-1000 ease-out"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(-40px)',
              transitionDelay: '400ms',
            }}
          >
            <div className="relative">
              {/* Gold frame accent */}
              <div className="absolute -top-3 -left-3 w-16 h-16 border-t border-l border-[#C9A84C]/50" />
              <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b border-r border-[#C9A84C]/50" />

              <div className="relative overflow-hidden rounded-lg w-full h-[420px] md:h-[520px]">
                <img
                  src="https://readdy.ai/api/search-image?query=intimate%20luxury%20lounge%20interior%20warm%20amber%20lighting%20dark%20wood%20elegant%20Japanese%20night%20bar%20sophisticated%20atmosphere%20deep%20navy%20walls%20gold%20accents%20cozy%20seating%20area%20premium%20establishment&width=700&height=900&seq=concept001&orientation=portrait"
                  alt="Lounge華 店内"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/60 via-transparent to-transparent" />
                {/* Since badge */}
                <div className="absolute top-4 left-4 border border-[#C9A84C]/60 px-3 py-1">
                  <span
                    className="text-[#C9A84C] text-xs tracking-[0.3em]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Since 2024
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p
                    className="text-white text-sm tracking-widest"
                    style={{ fontFamily: "'Shippori Mincho', serif" }}
                  >
                    富山市総曲輪
                  </p>
                  <p
                    className="text-white/60 text-xs mt-1"
                    style={{ fontFamily: "'Shippori Mincho', serif" }}
                  >
                    センタービル 3階
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gold line */}
        <div className="flex justify-center mt-16">
          <div
            className="h-px bg-[#C9A84C]/30 transition-all duration-1000 ease-out"
            style={{ width: inView ? '200px' : '0px', transitionDelay: '600ms' }}
          />
        </div>
      </div>
    </section>
  );
}
